import { cva, type VariantProps } from "class-variance-authority";

import { cn } from "@/utils/cn";

const Empty = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="empty"
      className={cn(
        "flex min-w-0 flex-1 flex-col items-center justify-center gap-6 rounded-lg border-dashed p-6 text-center text-balance md:p-12",
        className
      )}
      {...props}
    />
  );
};

const EmptyHeader = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="empty-header"
      className={cn(
        "flex max-w-sm flex-col items-center gap-2 text-center",
        className
      )}
      {...props}
    />
  );
};

const emptyMediaVariants = cva(
  "flex shrink-0 items-center justify-center mb-2 [&_svg]:pointer-events-none [&_svg]:shrink-0",
  {
    variants: {
      variant: {
        default: "bg-transparent",
        icon: "bg-primary/10 text-foreground flex text-primary shrink-0 items-center justify-center rounded-lg [&_svg:not([class*='size-'])]:size-11 p-3",
      },
    },
    defaultVariants: {
      variant: "default",
    },
  }
);

const EmptyMedia = ({
  className,
  variant = "default",
  ...props
}: React.ComponentProps<"div"> & VariantProps<typeof emptyMediaVariants>) => {
  return (
    <div
      data-slot="empty-icon"
      data-variant={variant}
      className={cn(emptyMediaVariants({ variant, className }))}
      {...props}
    />
  );
};

const EmptyTitle = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="empty-title"
      className={cn("text-lg font-medium tracking-tight", className)}
      {...props}
    />
  );
};

const EmptyDescription = ({
  className,
  ...props
}: React.ComponentProps<"p">) => {
  return (
    <div
      data-slot="empty-description"
      className={cn(
        "text-black/80 [&>a:hover]:text-primary text-sm/relaxed [&>a]:underline [&>a]:underline-offset-4 leading-snug",
        className
      )}
      {...props}
    />
  );
};

const EmptyContent = ({ className, ...props }: React.ComponentProps<"div">) => {
  return (
    <div
      data-slot="empty-content"
      className={cn(
        "flex w-full max-w-sm min-w-0 flex-col items-center gap-4 text-sm text-balance",
        className
      )}
      {...props}
    />
  );
};

export {
  Empty,
  EmptyHeader,
  EmptyTitle,
  EmptyDescription,
  EmptyContent,
  EmptyMedia,
};
